import { WithAuthorization } from "../../context";
import { Page } from "../../components/Page";
import { Card } from "../../components/Card";
import { DataTable } from "../../components/DataTable";
import * as React from "react";
import { useMemo, useState } from "react";
import { Button, Flex, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useGetSupplierNotes } from "../../hooks/query";
import {
  calculateDataTableState,
  constructQueryParamsString,
  handlePageSizeChange,
  showErrorToast
} from "../../utils";
import ViewSupplierNoteModal from "./components/ViewSupplierNoteModal";

function SupplierUserNotes() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierNotes"
  });
  const toast = useToast();
  const [notesModalData, setNotesModalData] = useState(null);
  const [notesList, setNotesList] = useState([]);

  const {
    isOpen: isNotesModalOpen,
    onOpen: onNotesModalOpen,
    onClose: onNotesModalClose
  } = useDisclosure();

  const closeNotesModal = () => {
    setNotesModalData(null);
    onNotesModalClose();
  };

  const columns = useMemo(() => {
      function handleViewNote(el) {
        setNotesModalData(el.row.original);
        onNotesModalOpen();
      }

      return [
        {
          header: t("gridHeaders.supplierNote"),
          accessorKey: "supplierNote",
          size: 250,
          skipNumericCheck: true,
          cell: (el) => {
            const cell = el.row.original;

            return (
              <Flex alignItems="center" gap={2}>
                <Text
                  minWidth={"130px"}
                  width={"90%"}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  >
                  {cell.supplierNote}
                </Text>
                <Button
                  minWidth={"130px"}
                  width={"10%"}
                  paddingInline={4}
                  title={t("labels.clickToSee")}
                  onClick={() => {
                    handleViewNote(el);
                  }}
                >
                  {t("buttons.viewNote")}
                </Button>
              </Flex>
            );
          }
        },
        {
          header: t("gridHeaders.status"),
          accessorKey: "status",
          enableColumnFilter: false
        }
      ];
    }, [t, onNotesModalOpen]
  );

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const {
    totalPages,
    filterObj,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const getQueryParamsString = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    return constructQueryParamsString(queryParams);
  };

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calculateDataTableState({
          prevState,
          newValObject
        })
      };
    });
  };

  const {
    isFetching: isFetchingNotes
  } = useGetSupplierNotes(getQueryParamsString(), {
      onSuccess: (data) => {
        const pageContent = data?.content ?? [];
        const notes = pageContent.map((el) => {
          return {
            id: el.id,
            supplierNote: el.supplierNote && el.supplierNote !== "" ?
              el.supplierNote : "n/a",
            status: el.status ? t(`status.${el.status}`) : "",
          };
        });
        setNotesList(notes);
        handlePageSizeChange(data, queryPageIndex, manageDataTableState);
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0
    }
  );

  return (
    <Page>
      <Card>
        <DataTable
          data={notesList}
          columns={columns}
          isLoading={isFetchingNotes}
          serverSidePagination={true}
          queryTotalCount={totalElements}
          queryPageCount={totalPages}
          queryPageIndex={queryPageIndex}
          setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
          queryPageSize={queryPageSize}
          setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
          containerHeight="var(--chakra-sizes-2xl)"
        />

        {isNotesModalOpen && <ViewSupplierNoteModal
          modalData={notesModalData}
          isOpen={isNotesModalOpen}
          onClose={closeNotesModal}
        />}
      </Card>
    </Page>
  );
}

export default WithAuthorization(SupplierUserNotes);